import { useState } from 'react';
import Swal from 'sweetalert2';
import { Modal, ModalBody, ModalFooter, Input, Textarea, Button, Alert } from '@components/ui';
import { DocumentDuplicateIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useGenerateApiKey } from '../hooks/useApiKeys';
import type { GeneratedApiKey } from '../types/apikey.types';

interface GenerateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormErrors {
  name?: string;
  description?: string;
}

const EMPTY_FORM = { name: '', description: '' };

export function GenerateApiKeyModal({ isOpen, onClose }: GenerateApiKeyModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [generated, setGenerated] = useState<GeneratedApiKey | null>(null);
  const generateMutation = useGenerateApiKey();

  const resetAndClose = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setGenerated(null);
    onClose();
  };

  // Mensajes en español correctamente mapeados a los campos reales (Name/Description) —
  // el original los mapeaba a claves inexistentes (username/password) y nunca se mostraban.
  const validate = (): boolean => {
    const nextErrors: FormErrors = {};
    if (!form.name.trim()) {
      nextErrors.name = 'Favor ingrese nombre.';
    } else if (form.name.trim().length < 5) {
      nextErrors.name = 'El campo Nombre debe tener al menos 5 caracteres.';
    }

    if (!form.description.trim()) {
      nextErrors.description = 'Favor ingrese descripción.';
    } else if (form.description.trim().length < 10) {
      nextErrors.description = 'El campo Descripción debe tener al menos 10 caracteres.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const result = await generateMutation.mutateAsync({
        name: form.name.trim(),
        description: form.description.trim(),
      });
      setGenerated(result);
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo generar el Api Key',
        text: 'Ocurrió un error inesperado. Intenta nuevamente.',
      });
    }
  };

  const handleCopy = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated.apiKey);
      Swal.fire({
        icon: 'success',
        title: 'Copiado! El Api Key ha sido copiado al portapapeles!',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
      });
    } catch {
      /* clipboard puede fallar si el navegador no tiene permiso — se ignora silenciosamente */
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      kicker="Integraciones · API"
      title={generated ? 'Api Key Generado' : 'Generar Nuevo Api Key'}
      icon={<KeyIcon />}
      size="md"
      closeTestId="api-key-generate-modal-close-button"
    >
      {generated ? (
        <>
          <ModalBody className="h-auto">
            <Alert variant="warning" className="mb-4" data-testid="api-key-generate-modal-warning-alert">
              Asegúrese de copiar y guardar este token. No será mostrado otra vez después de salir de esta ventana.
            </Alert>
            <Input label="Api Key" value={generated.apiKey} readOnly  data-testid="api-key-generate-modal-api-key-input"/>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" leftIcon={<DocumentDuplicateIcon className="w-4 h-4" />} onClick={handleCopy} data-testid="api-key-generate-modal-copy-button">
              Copiar
            </Button>
            <Button variant="primary" onClick={resetAndClose}>
              Cerrar
            </Button>
          </ModalFooter>
        </>
      ) : (
        <form onSubmit={handleSubmit} data-testid="api-key-generate-modal-form">
          <ModalBody className="h-auto space-y-4">
            <Input
              label="Nombre"
              required
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              data-testid="api-key-generate-modal-name-input"
              error={errors.name}
            />
            <Textarea
              label="Descripción"
              required
              rows={3}
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              data-testid="api-key-generate-modal-description-textarea"
              error={errors.description}
            />
          </ModalBody>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={resetAndClose} data-testid="api-key-generate-modal-cancel-button">
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={generateMutation.isPending} data-testid="api-key-generate-modal-generate-button">
              Generar
            </Button>
          </ModalFooter>
        </form>
      )}
    </Modal>
  );
}
